/**
 * Thin Tailwind-styled wrapper around Radix tabs.
 *
 * Used by ChassisDetail (Inventory / Backups / Alarms / Activity).
 * Radix handles keyboard navigation (Arrow keys, Home/End) and
 * focus management; we only style.
 *
 * Selected tab is exposed both as a controlled prop (so the parent
 * can mirror to the URL hash for deep-linkable tabs) and falls back
 * to uncontrolled if no ``value``/``onValueChange`` is supplied.
 */
import * as RxTabs from "@radix-ui/react-tabs";
import type { ReactNode } from "react";

export type TabSpec = {
  value: string;
  label: ReactNode;
  /** Optional badge / count after the label (e.g. unack alarm count). */
  badge?: ReactNode;
  content: ReactNode;
};

export function Tabs({
  tabs,
  value,
  defaultValue,
  onValueChange,
}: {
  tabs: TabSpec[];
  value?: string;
  defaultValue?: string;
  onValueChange?: (next: string) => void;
}) {
  return (
    <RxTabs.Root
      value={value}
      defaultValue={defaultValue ?? tabs[0]?.value}
      onValueChange={onValueChange}
      className="flex flex-col gap-3"
    >
      <RxTabs.List
        className={[
          // Concentric radius: p-1 (4px) + rounded (4px) triggers → rounded-lg
          // (8px) list. Depth comes from the shadow-border token, not a border.
          "inline-flex gap-1 rounded-lg p-1 self-start",
          "bg-slate-900 shadow-border",
        ].join(" ")}
      >
        {tabs.map((t) => (
          <RxTabs.Trigger
            key={t.value}
            value={t.value}
            className={[
              "inline-flex items-center gap-1.5 rounded px-3 py-1.5",
              "text-sm text-slate-400",
              "transition-[scale,background-color,color,box-shadow] duration-150 ease-out",
              "hover:text-slate-100 active:scale-[0.96]",
              "data-[state=active]:bg-slate-800 data-[state=active]:text-slate-100",
              "data-[state=active]:shadow-inner",
              "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-emerald-500",
            ].join(" ")}
          >
            <span>{t.label}</span>
            {t.badge}
          </RxTabs.Trigger>
        ))}
      </RxTabs.List>
      {tabs.map((t) => (
        <RxTabs.Content
          key={t.value}
          value={t.value}
          className="focus-visible:outline-none"
        >
          {t.content}
        </RxTabs.Content>
      ))}
    </RxTabs.Root>
  );
}
